import React, { useCallback, useState } from 'react';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { ReactComponent as SortArrow } from '../../../shared/img/chevron-up-down.svg';
import VendorModel from './models/VendorModel';
import VendorModal from './VendorModal';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { logAuditEvent } from '../../../utils/auditLogger';
import { buildVendorSavePayload, mapVendorFromApi } from './utils/mapVendor';
import { toBooleanFromBit } from './utils/vendorHelpers';
import { validateVendor } from './vendorValidation';

function VendorGrid({
  VendorData,
  fetchVendors,
  UserPermissions = {},
  moduleName = 'Vendor',
}) {
  const [actionType, setActionType] = useState();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [vendorModel, setVendorModel] = useState(new VendorModel());
  const [filterText, setFilterText] = useState('');
  const [alert, setAlert] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const isRowActive = (row) => toBooleanFromBit(row?.IsActive, false);

  const closeModal = () => {
    setIsOpen(false);
    fetchVendors();
  };

  const rows = Array.isArray(VendorData)
    ? VendorData.filter((item) => {
        const search = filterText.toLowerCase();
        return (
          (item.VendorName && item.VendorName.toLowerCase().includes(search)) ||
          (item.Email && item.Email.toLowerCase().includes(search)) ||
          (item.ContactNumber && item.ContactNumber.toLowerCase().includes(search))
        );
      })
    : [];

  const loadVendorDetail = async (vendorId) => {
    const response = await axios.get(`${getApiBaseUrl()}/ec-vendor/getById`, {
      params: { VendorId: vendorId },
    });
    return mapVendorFromApi(response.data?.data);
  };

  const openActionType = useCallback(
    async (row, name) => {
      const detail = row?.VendorName ? `Vendor: ${row.VendorName}` : '';
      logAuditEvent(moduleName, `${name} Click`, `${name} clicked. ${detail}`.trim());
      setLoadingDetail(true);
      try {
        const full = row?.VendorId ? await loadVendorDetail(row.VendorId) : row;
        setVendorModel(full || new VendorModel());
        setActionType(name);
        setIsOpen(true);
      } catch (err) {
        console.error(err);
        CreateAlert('Failed to load vendor details', 'Error', setAlert);
      } finally {
        setLoadingDetail(false);
      }
    },
    [moduleName]
  );

  const toggleActive = useCallback(
    async (row) => {
      if (UserPermissions.EditModule !== 1 || !row?.VendorId) return;

      const nextActive = !isRowActive(row);
      setTogglingId(row.VendorId);
      logAuditEvent(
        moduleName,
        'Toggle Active',
        `Vendor ${row.VendorName} set to ${nextActive ? 'active' : 'inactive'}`
      );

      try {
        const payload = buildVendorSavePayload(
          { ...mapVendorFromApi(row), IsActive: nextActive },
          getStorageValues('userId')
        );
        const response = await axios.post(`${getApiBaseUrl()}/ec-vendor/save`, payload);
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(
            result.message || `Vendor ${nextActive ? 'activated' : 'deactivated'} successfully!!`,
            'Success',
            setAlert
          );
          fetchVendors();
        } else {
          CreateAlert(result?.message || 'Failed to update vendor status!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to update vendor status!!', 'Error', setAlert);
      } finally {
        setTogglingId(null);
      }
    },
    [UserPermissions.EditModule, fetchVendors, moduleName]
  );

  const onSubmit = async (e, data) => {
    if (e.target.name === 'Edit') {
      const payload = buildVendorSavePayload(data, getStorageValues('userId'));
      if (!validateVendor(payload, setAlert)) return;
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-vendor/save`, payload);
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Vendor updated successfully!!', 'Success', setAlert);
          fetchVendors();
          closeModal();
        } else {
          CreateAlert(result?.message || 'Failed to update vendor!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to update vendor!!', 'Error', setAlert);
      }
    } else if (e.target.name === 'Delete') {
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-vendor/delete`, {
          VendorId: vendorModel.VendorId,
          UserId: getStorageValues('userId'),
        });
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Vendor deleted successfully!!', 'Success', setAlert);
          fetchVendors();
          closeModal();
        } else {
          CreateAlert(result?.message || 'Failed to delete vendor!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to delete vendor!!', 'Error', setAlert);
      }
    }
  };

  const columns = [
    {
      name: (
        <div className="titlename">
          <span>Vendor Name</span>
        </div>
      ),
      selector: (row) => row.VendorName,
      cell: (row) => <span title={row.VendorName}>{row.VendorName}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Email</span>
        </div>
      ),
      selector: (row) => row.Email,
      cell: (row) => <span title={row.Email}>{row.Email || '-'}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Contact Number</span>
        </div>
      ),
      selector: (row) => row.ContactNumber,
      cell: (row) => <span title={row.ContactNumber}>{row.ContactNumber || '-'}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Rating</span>
        </div>
      ),
      selector: (row) => row.Rating,
      cell: (row) => <span>{row.Rating ?? '-'}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
      width: '100px',
    },
    {
      name: (
        <div className="titlename">
          <span>Review Count</span>
        </div>
      ),
      selector: (row) => row.ReviewCount,
      sortable: true,
      sortIcon: <SortArrow />,
      width: '130px',
    },
    {
      name: (
        <div className="titlename">
          <span>Active</span>
        </div>
      ),
      cell: (row) => (
        <div className="status-toggle modal-status">
          <input
            type="checkbox"
            id={`vendor-active-${row.VendorId}`}
            className="check"
            checked={isRowActive(row)}
            disabled={UserPermissions.EditModule !== 1 || togglingId === row.VendorId}
            onChange={() => toggleActive(row)}
          />
          <label htmlFor={`vendor-active-${row.VendorId}`} className="checktoggle" />
        </div>
      ),
      width: '100px',
    },
  ];

  if (UserPermissions.ViewModule === 1 || UserPermissions.EditModule === 1 || UserPermissions.DeleteModule === 1) {
    columns.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          {UserPermissions.ViewModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'View')}>
              <Icons.Eye className="action-eye" />
            </Link>
          ) : null}
          {UserPermissions.EditModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'Edit')}>
              <Icons.Edit className="feather-edit" />
            </Link>
          ) : null}
          {UserPermissions.DeleteModule === 1 ? (
            <Link className="confirm-text p-2" to="" onClick={() => openActionType(row, 'Delete')}>
              <Icons.Trash2 className="feather-trash-2" />
            </Link>
          ) : null}
        </div>
      ),
      center: 'true',
      width: '150px',
    });
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search-vendor"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="VendorName"
            sortIcon={<SortArrow />}
            persistTableHead
            progressPending={loadingDetail}
          />
        </div>
      </div>
      <VendorModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={vendorModel}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default VendorGrid;
